import type {
  CreateSOWRequest,
  CreateSOWResponse,
  GetSOWResponse,
  ListSOWsResponse,
  CreateChangeRequest,
  CreateChangeResponse,
  AcknowledgeChangeResponse,
  ErrorResponse,
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  RegisterResponse,
  GetMeResponse,
  ForgotPasswordRequest,
  ForgotPasswordResponse,
  ResetPasswordRequest,
  ResetPasswordResponse,
  CreateCheckoutRequest,
  CreateCheckoutResponse,
  GetSubscriptionResponse,
} from '@/types';
import { useAuthStore } from '@/stores/authStore';

const API_BASE = import.meta.env.DEV
  ? '/api' // Proxied by Vite in dev
  : (import.meta.env.VITE_API_URL || 'https://vistora-backend.vistorahq.workers.dev/api');

class APIError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'APIError';
  }
}

interface RequestOptions extends RequestInit {
  skipAuth?: boolean;
}

async function request<T>(
  endpoint: string,
  options?: RequestOptions
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options?.headers as Record<string, string>),
  };

  // Add auth token if available and not skipping auth
  if (!options?.skipAuth) {
    const token = useAuthStore.getState().token;
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }

  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers,
  });

  // Handle 401 - clear auth state (only for authenticated requests)
  if (response.status === 401 && !options?.skipAuth) {
    useAuthStore.getState().clearAuth();
    throw new APIError(401, 'Session expired. Please log in again.');
  }

  if (!response.ok) {
    let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
    try {
      const error: ErrorResponse = await response.json();
      errorMessage = error.error;
    } catch {
      // Response body is not JSON, use default error message
    }
    throw new APIError(response.status, errorMessage);
  }

  return response.json();
}

export const api = {
  // Auth endpoints
  auth: {
    login: (data: LoginRequest) =>
      request<LoginResponse>('/auth/login', {
        method: 'POST',
        body: JSON.stringify(data),
        skipAuth: true,
      }),

    register: (data: RegisterRequest) =>
      request<RegisterResponse>('/auth/register', {
        method: 'POST',
        body: JSON.stringify(data),
        skipAuth: true,
      }),

    me: () => request<GetMeResponse>('/auth/me'),

    forgotPassword: (data: ForgotPasswordRequest) =>
      request<ForgotPasswordResponse>('/auth/forgot-password', {
        method: 'POST',
        body: JSON.stringify(data),
        skipAuth: true,
      }),

    resetPassword: (data: ResetPasswordRequest) =>
      request<ResetPasswordResponse>('/auth/reset-password', {
        method: 'POST',
        body: JSON.stringify(data),
        skipAuth: true,
      }),
  },

  // Owner endpoints (authenticated)
  listSOWs: () => request<ListSOWsResponse>('/sows'),

  createSOW: (data: CreateSOWRequest) =>
    request<CreateSOWResponse>('/sows', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  getSOW: (id: string) => request<GetSOWResponse>(`/sows/${id}`),

  createChange: (sowId: string, data: CreateChangeRequest) =>
    request<CreateChangeResponse>(`/sows/${sowId}/changes`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Client endpoints (public, token-based)
  getClientSOW: (token: string) =>
    request<GetSOWResponse>(`/client/sows/${token}`, { skipAuth: true }),

  acknowledgeChange: (token: string, changeId: string) =>
    request<AcknowledgeChangeResponse>(
      `/client/sows/${token}/acknowledge/${changeId}`,
      { method: 'POST', skipAuth: true }
    ),

  // Billing endpoints (authenticated)
  createCheckout: (data: CreateCheckoutRequest) =>
    request<CreateCheckoutResponse>('/checkout', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  getSubscription: () => request<GetSubscriptionResponse>('/subscription'),

  getCustomerPortalUrl: () =>
    request<{ data: { url: string } }>('/payment/customer-portal', {
      method: 'POST',
    }),

  // GDPR endpoints (authenticated)
  exportUserData: () =>
    request<{ data: any }>('/user/export'),

  deleteAccount: () =>
    request<{ data: { message: string } }>('/user', {
      method: 'DELETE',
    }),
};

export { APIError };
